<template>
  <v-dialog
    :value="value"
    max-width="800"
    @input="$emit('input', $event)"
  >
    <v-card class="pa-4">
      <v-btn
        icon
        class="dialog-close"
        @click="$emit('input', false)"
      >
        <v-icon size="32">
          mdi-close
        </v-icon>
      </v-btn>

      <div class="text-left mb-4">
        <div class="text-h5 font-weight-bold black--text mb-2">
          รายละเอียดเอกสารแนบ
        </div>
        <div class="text-body-1 black--text">
          วันที่แนบเอกสาร <spen class="green--text font-weight-bold">
            {{ formatDate(rejectedAt) }}
          </spen>
        </div>
        <div class="text-body-1 black--text">
          เวลาที่แนบเอกสาร <spen class="green--text font-weight-bold">
            {{ formatTime(rejectedAt) }}
          </spen>
        </div>
      </div>

      <v-divider></v-divider>

      <div class="mt-4 text-h5 text-left">
        Preview
      </div>

      <template v-if="loading">
        <div class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary" />
        </div>
      </template>

      <template v-else-if="files && files.length > 0">
        <!-- Preview section -->
        <div class="d-flex flex-column align-center my-4">
          <v-img
            v-if="isImageFile(currentFile.contentType) && getFilePreviewUrl(currentFile)"
            key="file-preview-img"
            class="mb-4"
            :src="getFilePreviewUrl(currentFile)"
            max-width="500"
            max-height="400"
            contain
          >
            <template v-slot:placeholder>
              <v-row
                class="fill-height ma-0"
                align="center"
                justify="center"
              >
                <v-progress-circular
                  indeterminate
                  color="grey lighten-5"
                />
              </v-row>
            </template>
          </v-img>

          <!-- PDF preview using iframe with blob -->
          <!-- <div
            v-else-if="isPdfFile(currentFile.contentType) && getFilePreviewUrl(currentFile)"
            class="pdf-preview mb-4"
          >
            <div
              v-if="loadingPdfs[currentFile.fileId]"
              class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary" />
              <div class="mt-2">
                กำลังโหลด PDF...
              </div>
            </div>
            <iframe
              v-else
              :src="pdfObjectUrls[currentFile.fileId]"
              width="100%"
              height="600px"
              frameborder="0"
              class="pdf-iframe"
            >
            </iframe>
          </div> -->

          <!-- Document preview placeholder for non-image, non-PDF files -->
          <v-card
            v-else
            outlined
            class="text-center pa-8 mb-4"
            style="min-width: 300px; min-height: 200px;"
          >
            <v-icon
              size="64"
              color="primary"
              class="mb-2"
            >
              {{ getFileIcon(currentFile.contentType) }}
            </v-icon>
            <div class="text-h6 primary--text">
              {{ getFileTypeText(currentFile.contentType) }}
            </div>
            <div class="grey--text">
              ไม่สามารถแสดงตัวอย่างได้
            </div>
          </v-card>

          <!-- Pagination -->
          <v-pagination
            v-if="files.length > 1"
            v-model="page"
            class="mb-4"
            :length="files.length"
          />

          <v-btn
            rounded
            color="primary"
            min-width="200"
            :loading="downloadingFiles[currentFile.fileId]"
            @click="downloadFile(currentFile)"
          >
            Download File
          </v-btn>
        </div>
      </template>

      <v-card
        v-else
        outlined
        class="text-center pa-8"
      >
        <v-icon
          size="64"
          color="grey"
          class="mb-2"
        >
          mdi-file-document-outline
        </v-icon>
        <div class="grey--text">
          ไม่พบเอกสารแนบ
        </div>
      </v-card>
    </v-card>
  </v-dialog>
</template>

<script>
import { OrderService } from '@/providers'
import axios from 'axios'

export default {
  name: 'WithholdingTaxFilesDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    orderId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      page: 1,
      downloadingFiles: {},
      fileUrls: {},
      rejectedAt: null,
      filesExpiredAt: null,
      files: [],
      loading: false,
      pdfObjectUrls: {},
      loadingPdfs: {}
    }
  },
  computed: {
    currentIndex() {
      return this.page - 1
    },
    currentFile() {
      return this.files && this.files.length > 0 ? this.files[this.currentIndex] : null
    }
  },
  watch: {
    async value(newVal) {
      if (newVal && this.files.length === 0) {
        await this.loadFilesAndUrls()
      }
    }
  },
  beforeDestroy() {
    // Clean up object URLs to prevent memory leaks
    Object.values(this.pdfObjectUrls).forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url)
      }
    })
  },
  methods: {
    async loadFilesAndUrls() {
      try {
        this.loading = true
        const response = await OrderService.fetchWithholdingTaxRejectionFilesUrls(this.orderId)

        // Set files data
        this.files = response.files || []

        // Map URLs by fileId for easy lookup
        response.files.forEach((file) => {
          this.$set(this.fileUrls, file.fileId, file.url)
        })

        this.rejectedAt = response.rejectedAt
        this.filesExpiredAt = response.expiresAt

        // Auto-load PDF blobs for PDF files
        this.files.forEach((file) => {
          if (this.isPdfFile(file.contentType)) {
            this.loadPdfBlob(file)
          }
        })
      } catch (error) {
        if (error.response?.status === 410) {
          // Handle expired files
          this.handleExpiredFiles()
        } else if (error.response?.status === 404) {
          // console.error('No rejection files found for order:', this.orderId)
        } else {
          // console.error('Error loading file URLs:', error)
        }
      } finally {
        this.loading = false
      }
    },

    handleExpiredFiles() {
      this.$swal({
        title: 'ไฟล์หมดอายุ',
        text: 'ไฟล์เอกสารนี้หมดอายุแล้ว หากต้องการเอกสารติดต่อได้ที่\nlineagency@sellsuki.com หรือ LINE @linebysellsuki',
        icon: 'warning',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#4CAF50'
      })
    },

    getFilePreviewUrl(file) {
      return this.fileUrls[file.fileId]
    },

    async loadPdfBlob(file) {
      try {
        this.$set(this.loadingPdfs, file.fileId, true)

        const url = this.getFilePreviewUrl(file)
        if (!url) {
          throw new Error('File URL not available')
        }

        const response = await axios.get(url, {
          responseType: 'blob'
        })

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const objectUrl = URL.createObjectURL(blob)

        this.$set(this.pdfObjectUrls, file.fileId, objectUrl)
      } catch (error) {
        this.$set(this.pdfObjectUrls, file.fileId, '')
      } finally {
        this.$set(this.loadingPdfs, file.fileId, false)
      }
    },

    async downloadFile(file) {
      try {
        this.$set(this.downloadingFiles, file.fileId, true)

        // Use the already loaded URL or get it from batch
        const url = this.getFilePreviewUrl(file)
        if (!url) {
          throw new Error('File URL not available')
        }

        // Try to download with programmatic approach first
        try {
          // Test if we can fetch the URL (CORS check)
          await fetch(url, { method: 'HEAD', mode: 'cors' })

          // If CORS is allowed, use the traditional download method
          const link = document.createElement('a')
          link.href = url
          link.download = `document_${file.fileId}.${this.getFileExtension(file.contentType)}`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } catch (corsError) {
          // CORS blocked - fallback to window.open
          // console.error('CORS blocked, falling back to window.open:', corsError)
          window.open(url, '_blank')
        }
      } catch (error) {
        // console.error('Error downloading file:', error)
        this.$swal({
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถดาวน์โหลดไฟล์ได้',
          icon: 'error'
        })
      } finally {
        this.$set(this.downloadingFiles, file.fileId, false)
      }
    },

    isImageFile(contentType) {
      return /^image\/(jpeg|jpg|png|gif)$/i.test(contentType)
    },

    isPdfFile(contentType) {
      return contentType === 'application/pdf'
    },

    isBlobUrl(url) {
      return url && url.startsWith('blob:')
    },

    getFileIcon(contentType) {
      const iconMap = {
        'application/pdf': 'mdi-file-pdf',
        'application/msword': 'mdi-file-word',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'mdi-file-word',
        'application/vnd.ms-excel': 'mdi-file-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'mdi-file-excel'
      }
      return iconMap[contentType] || 'mdi-file-document'
    },

    getFileTypeText(contentType) {
      const typeMap = {
        'application/pdf': 'PDF Document',
        'application/msword': 'Word Document',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
        'image/jpeg': 'JPEG Image',
        'image/jpg': 'JPG Image',
        'image/png': 'PNG Image',
        'image/gif': 'GIF Image'
      }
      return typeMap[contentType] || 'Document'
    },

    getFileExtension(contentType) {
      const extensions = {
        'application/pdf': 'pdf',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
        'image/gif': 'gif',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx'
      }
      return extensions[contentType] || 'file'
    },

    formatDate(date) {
      if (!date) return ''
      const dateObj = new Date(date)
      const day = dateObj.getDate().toString().padStart(2, '0')
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0')
      const year = dateObj.getFullYear()
      return `${day}-${month}-${year}`
    },

    formatTime(date) {
      if (!date) return ''
      return new Date(date).toLocaleTimeString('th-TH', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.dialog-close {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
}
.pdf-preview {
  width: 100%;
  max-width: 800px;
}

.pdf-iframe {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
